/** One native Back owner for the WebView, including lazy/nested dialogs.
 * No synthetic browser history, polling, storage, or backend requests.
 * Telegram routes Android's system Back to BackButton when it is visible.
 */
export interface NativeBackButton {
  show(): void;
  hide(): void;
  onClick(callback: () => void): void;
  offClick(callback: () => void): void;
}

export class DialogBackStack<T extends { open: boolean; isConnected: boolean }> {
  private entries: T[] = [];
  opened(dialog: T): void { this.closed(dialog); this.entries.push(dialog); }
  closed(dialog: T): void { this.entries = this.entries.filter(item => item !== dialog); }
  top(): T | undefined {
    this.entries = this.entries.filter(item => item.open && item.isConnected);
    return this.entries.at(-1);
  }
}

const installed = new WeakMap<Document, { button: NativeBackButton; stop(): void; root(handler: () => void): void; menu(key: string, handler?: () => void, owner?: HTMLElement): void }>();

/** Popovers are above their own screen, but below an unrelated nested modal. */
export function setBackMenu(key: string, handler?: () => void, doc: Document = document, owner?: HTMLElement): void {
  installed.get(doc)?.menu(key, handler, owner);
}

export function installBackNavigation(button: NativeBackButton | undefined, doc: Document = document, rootBack?: () => void): void {
  if (!button) return;
  const previous = installed.get(doc);
  if (previous?.button === button) { if (rootBack) previous.root(rootBack); return; }
  previous?.stop();
  const stack = new DialogBackStack<HTMLDialogElement>();
  const menus = new Map<string, {handler: () => void; owner?: HTMLElement}>();
  const isDialog = (node: Node): node is HTMLDialogElement => node.nodeType === 1 && (node as Element).tagName === 'DIALOG';
  let visible: boolean | undefined, dispatching = false;
  const sync = () => {
    const next = Boolean(stack.top() || menus.size || rootBack);
    if (next === visible) return;
    visible = next;
    if (next) button.show(); else button.hide();
  };
  const process = (records: MutationRecord[]) => {
    for (const record of records) {
      if (record.type === 'attributes' && isDialog(record.target)) {
        // Replay close/reopen within one microtask in actual opening order.
        if (record.oldValue === null) stack.opened(record.target);
        else stack.closed(record.target);
      } else if (record.type === 'childList') {
        for (const node of record.addedNodes) {
          if (isDialog(node) && node.open) stack.opened(node);
          else if (node.nodeType === 1) {
            (node as Element).querySelectorAll<HTMLDialogElement>('dialog[open]').forEach(dialog => stack.opened(dialog));
          }
        }
      }
    }
    sync();
  };
  const observer = new MutationObserver(process);
  const back = () => {
    if (dispatching) return;
    dispatching = true;
    try {
      process(observer.takeRecords());
      const dialog = stack.top();
      const ownedMenu=dialog?Array.from(menus.values()).reverse().find(menu=>menu.owner&&dialog.contains(menu.owner)):undefined;
      if(ownedMenu)ownedMenu.handler();
      else if (dialog) {
        // Same cancel path as Escape: preserve save/upload guards, focus and
        // resource cleanup. Never close an underlying layer in the same press.
        const allowed = dialog.dispatchEvent(new Event('cancel', { cancelable: true }));
        if (allowed && dialog.open) dialog.close();
      } else (Array.from(menus.values()).at(-1)?.handler ?? rootBack)?.();
      process(observer.takeRecords());
    } finally { dispatching = false; }
  };
  doc.querySelectorAll<HTMLDialogElement>('dialog[open]').forEach(dialog => stack.opened(dialog));
  observer.observe(doc.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'], attributeOldValue: true });
  button.onClick(back);
  installed.set(doc, {
    button,
    root(handler) { rootBack = handler; sync(); },
    menu(key, handler, owner) { menus.delete(key); if (handler) menus.set(key, {handler,owner}); sync(); },
    stop() { observer.disconnect(); button.offClick(back); button.hide(); installed.delete(doc); },
  });
  sync();
}
